import React from 'react';
import { Link } from 'react-router-dom';
import Modal from './Modal';
import { clientRecordPath, type ExistingClientRef } from '../api/clients';
import './DuplicateClientModal.css';

/**
 * Shown when a new client or lead is refused because another record already uses
 * the name. Names match exactly (case aside), so the record linked here really is
 * the same customer, not a similarly named one.
 */
const DuplicateClientModal: React.FC<{
  existing: ExistingClientRef | null;
  onClose: () => void;
}> = ({ existing, onClose }) => (
  <Modal isOpen={existing != null} onClose={onClose} title="That name is already in use" narrow>
    {existing ? (
      <>
        <div className="modal-body">
          <p className="duplicate-client__lead">
            {existing.type === 'lead' ? 'A lead' : 'A client'} named <strong>{existing.company}</strong> is already in
            the database.
          </p>
          <p className="duplicate-client__hint">
            Open the existing record to add to it, or change the name if this is a different company.
          </p>
          <Link className="duplicate-client__link" to={clientRecordPath(existing)} onClick={onClose}>
            Open {existing.company}
          </Link>
        </div>
        <div className="modal-actions">
          <button type="button" className="generate-contract-button" onClick={onClose}>
            Use a different name
          </button>
        </div>
      </>
    ) : null}
  </Modal>
);

/**
 * Inline version for forms that already live inside a modal, where stacking a
 * second one would be awkward.
 */
export const DuplicateClientAlert: React.FC<{ existing: ExistingClientRef }> = ({ existing }) => (
  <div className="duplicate-client__alert" role="alert">
    <span>
      {existing.type === 'lead' ? 'A lead' : 'A client'} named <strong>{existing.company}</strong> already exists.
    </span>
    <Link className="duplicate-client__link" to={clientRecordPath(existing)}>
      Open {existing.company}
    </Link>
  </div>
);

export default DuplicateClientModal;
